<template>
  <div class="page-section">
    <div class="background-color" />
    <div
      class="breakdown-table"
      :class="{ 'disabled': isDataMissing }"
    >
      <div class="breakdown-header">
        <img
          src="../../../../assets/etoro/graph-icon.svg"
          alt="Graph Icon"
          class="graph-icon"
        >
        <h2>Numbers breakdown</h2>
      </div>
      <div class="covered-months" v-if="(validCashflowMonths < 5 && !isDataMissing)">
      <img
        src="../../../../assets/black_lightbulb.svg"
        alt="info"
      >
      <p>
        The numbers are based on available data covering {{ validCashflowMonths }} of the past 5 months.
      </p>
    </div>
      <div
        v-if="isDataMissing"
        class="connect-another-subtitle missing-data-subtitle"
      >
        Unlock your numbers breakdown by <b @click="handleConnectAccountsClick">connecting your current account</b>
      </div>
      <div class="breakdown-content">
        <div class="breakdown-row">
          <div class="col source">
            Current account balance
          </div>
          <div class="col total">
            £{{ numberToHumanReadable(availableFunds.total) }}
          </div>
        </div>

        <div class="breakdown-row">
          <div class="col source">
            Average monthly net balance
          </div>
          <div class="col total">
            £{{ numberToHumanReadable(monthlyAvgDisposableIncome.total) }}
          </div>
        </div>

        <div class="breakdown-row breakdown-total-row">
          <div class="col source">
            Total amount
          </div>
          <div class="col total">
            £{{ numberToHumanReadable(availableFunds.total + monthlyAvgDisposableIncome.total) }}
          </div>
        </div>

        <div class="breakdown-row breakdown-investable-row breakdown-total-row">
          <div class="col source">
            Investable amount
          </div>
          <div class="col total">
            £{{ totalInvestable < 0 ? 0 : numberToHumanReadable(totalInvestable) }}
          </div>
        </div>
      </div>
      <button
        v-if="!isDataMissing"
        class="green-button"
        @click="navigateToEtoro"
      >
        {{ buttonText }}
      </button>
      <div
        v-if="!isDataMissing"
        class="connect-another-subtitle"
      >
        Numbers look off? To get accurate insights, connect the accounts and cards you use daily.
        <br>
        <b @click="handleConnectAccountsClick">connect another account</b>
      </div>
    </div>
    <div class="divider-line" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { numberToHumanReadable } from '../../../../utils/miscUtils';

const props = defineProps({
  availableFunds: {
    type: Object,
    required: true,
  },
  monthlyAvgDisposableIncome: {
    type: Object,
    required: true,
  },
  totalInvestable: {
    type: Number,
    required: true,
  },
  validCashflowMonths: {
    type: Number,
    default: 5,
  },
  isDataMissing: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['navigateToEtoro', 'connectAccountsClick']);

const buttonText = computed(() => (
  props.totalInvestable < 0
    ? 'Log in to your eToro wallet'
    : `Add £${numberToHumanReadable(props.totalInvestable)} to your eToro Wallet`
));

const navigateToEtoro = () => {
  emit('navigateToEtoro');
};

const handleConnectAccountsClick = () => {
  emit('connectAccountsClick');
};
</script>

<style scoped>
.page-section {
  color: white;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 20px;
}

.background-color {
  background-color: #F6F8ED;
  height: 100px;
  z-index: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.breakdown-table {
  width: 90%;
  background-color: white;
  border-radius: 25px;
  padding: 25px;
  margin-top: 20px;
  box-shadow: 0px 16px 46px rgba(22.80, 123.92, 42.57, 0.62);
  z-index: 2;
  margin: 0 auto;
  position: relative;
}

.breakdown-table.disabled {
  background-color: #F1F5FA !important;
  box-shadow: 0px 16px 46px rgba(22.80, 123.92, 42.57, 0.1) !important;
}

.breakdown-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.breakdown-header h2 {
  margin: 0;
  color: #205441;
  font-size: 20px;
  font-weight: 700;
  margin-left: 10px;
}

.breakdown-header .graph-icon {
  width: 20px;
  height: 20px;
}

.missing-data-subtitle {
  background-color: #FFDC73 !important;
  text-align: center;
  margin-bottom: 20px;
  font-size: 14px;
  font-family: CircularStdBook;
  padding: 20px;
  border-radius: 16px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  color: #205441;
}

.breakdown-content {
  width: 100%;
}

.breakdown-row {
  display: flex;
  padding: 18px 5px;
  border-bottom: 1px solid rgba(32, 84, 65, 0.2);
  font-family: CircularStdBook;
}

.breakdown-row:nth-child(2) {
  border-bottom: 1px solid rgba(32, 84, 65, 0.2);
}

.breakdown-row:last-child {
  border-bottom: none;
}

.col {
  color: #333;

  .disabled & {
    color: #B0B0B0 !important;
  }
}

.col.source {
  flex: 3;
  text-align: left;
}

.col.total {
  flex: 1;
  text-align: right;
}

.breakdown-total-row {
  font-weight: 700;
}

.breakdown-investable-row .col.total {
  color: #13C636;

  .disabled & {
    color: #B0B0B0 !important;
  }
}

.green-button {
  background-color: #205441;
  box-shadow: 0px 4px 21px rgba(149.13, 152.94, 149.88, 0.56);
  border-radius: 12px;
  color: white;
  padding: 17px 0px;
  border: none;
  width: 100%;
  cursor: pointer;
  font-size: 18px;
  font-family: CircularStd;
  font-weight: 700;
  margin: 20px auto;
  display: block;
}

.connect-another-subtitle {
  font-size: 14px;
  font-family: CircularStdBook;
  margin-bottom: 30px;
  margin-top: 10px;
  background: #F6F8ED;
  color: #205441;
  padding: 20px;
  border-radius: 16px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  text-align: center;
  line-height: 1.3;
}

.connect-another-subtitle b {
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.divider-line {
  width: 90%;
  margin: 0 auto;
  margin-top: 30px;
  height: 1px;
  background-color: white;
  opacity: 0.5;
}

.covered-months {
  display: flex;
  gap: 12px;
  background-color: #F6F8ED;
  border-radius: 12px;
  padding: 16px;
  width: 100%;

  p {
    color: #205441;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }
}
</style>
